import isEmail from 'validator/lib/isEmail';

import { AgencyOfficerFormRow, createEmptyOfficerRow } from './agencySignupSchema';

export type AgencyRosterCsvRow = AgencyOfficerFormRow & {
  section: 'minOfficers' | 'maxOfficers';
};

export type TradeShowLeadCsvRow = {
  rank: string;
  fullName: string;
  agency: string;
  email: string;
};

export type CsvParseResult<TRow> = {
  rows: TRow[];
  errors: string[];
};

const REQUIRED_AGENCY_HEADERS = [
  'First Name',
  'Last Name',
  'Rank / Title',
  'Email Address',
  'Supervisor Name',
  'Supervisor Rank / Title',
  'Supervisor Email',
];

const TRADE_SHOW_HEADERS = ['Rank', 'Full Name', 'Agency', 'e-mail address'];

export const AGENCY_SIGNUP_ROSTER_TEMPLATE = [
  REQUIRED_AGENCY_HEADERS,
  ['Alex', 'Taylor', 'Officer', 'alex.taylor@agency.gov', 'Jordan Smith', 'Sergeant', 'jordan.smith@agency.gov'],
  ['Morgan', 'Lee', 'Detective', 'morgan.lee@agency.gov', 'Jordan Smith', 'Sergeant', 'jordan.smith@agency.gov'],
];

export const TRADE_SHOW_LEADS_TEMPLATE = [
  TRADE_SHOW_HEADERS,
  ['Chief', 'Avery Johnson', 'Benton County Sheriff', 'avery.johnson@benton.gov'],
];

export function parseAgencyRosterCsv(data: string[][]): CsvParseResult<AgencyRosterCsvRow> {
  const { headers, rows } = splitCsv(data);
  const columns = buildHeaderMap(headers);
  const errors: string[] = [];
  const parsedRows: AgencyRosterCsvRow[] = [];
  const seenEmails = new Set<string>();

  rows.forEach((row, index) => {
    if (isBlankRow(row))
      return;

    const line = index + 2;
    const fullName = readCell(row, columns, ['full name', 'name']);
    const [firstFromFullName, lastFromFullName] = splitFullName(fullName);
    const officer: AgencyRosterCsvRow = {
      ...createEmptyOfficerRow(),
      section: normalizeRosterSection(readCell(row, columns, ['section', 'pilot group', 'group'])),
      firstName: readCell(row, columns, ['first name', 'firstname']) || firstFromFullName,
      lastName: readCell(row, columns, ['last name', 'lastname']) || lastFromFullName,
      title: readCell(row, columns, ['rank / title', 'rank', 'title']),
      email: readCell(row, columns, ['email address', 'e-mail address', 'email']),
      supervisorName: readCell(row, columns, ['supervisor name']),
      supervisorTitle: readCell(row, columns, ['supervisor rank / title', 'supervisor rank', 'supervisor title']),
      supervisorEmail: readCell(row, columns, ['supervisor email', 'supervisor e-mail address']),
    };

    const missing = getMissingOfficerFields(officer);
    if (missing.length > 0) {
      errors.push(`Line ${line}: missing ${missing.join(', ')}`);
      return;
    }

    const normalizedEmail = officer.email.toLowerCase();
    const normalizedSupervisorEmail = officer.supervisorEmail.toLowerCase();

    if (!isEmail(normalizedEmail)) {
      errors.push(`Line ${line}: ${officer.email} is not a valid officer email`);
      return;
    }

    if (!isEmail(normalizedSupervisorEmail)) {
      errors.push(`Line ${line}: ${officer.supervisorEmail} is not a valid supervisor email`);
      return;
    }

    if (seenEmails.has(normalizedEmail)) {
      errors.push(`Line ${line}: duplicate officer email ${officer.email}`);
      return;
    }

    seenEmails.add(normalizedEmail);
    parsedRows.push({
      ...officer,
      email: normalizedEmail,
      supervisorEmail: normalizedSupervisorEmail,
    });
  });

  return { rows: parsedRows, errors };
}

export function parseTradeShowLeadsCsv(data: string[][]): CsvParseResult<TradeShowLeadCsvRow> {
  const { headers, rows } = splitCsv(data);
  const columns = buildHeaderMap(headers);
  const errors: string[] = [];
  const parsedRows: TradeShowLeadCsvRow[] = [];
  const seenEmails = new Set<string>();

  rows.forEach((row, index) => {
    if (isBlankRow(row))
      return;

    const line = index + 2;
    const lead: TradeShowLeadCsvRow = {
      rank: readCell(row, columns, ['rank', 'title']),
      fullName: readCell(row, columns, ['full name', 'name']),
      agency: readCell(row, columns, ['agency', 'library', 'department']),
      email: readCell(row, columns, ['e-mail address', 'email address', 'email']),
    };

    const missing = Object.entries(lead)
      .filter(([_key, value]) => !value)
      .map(([key]) => key);
    if (missing.length > 0) {
      errors.push(`Line ${line}: missing ${missing.join(', ')}`);
      return;
    }

    const normalizedEmail = lead.email.toLowerCase();
    if (!isEmail(normalizedEmail)) {
      errors.push(`Line ${line}: ${lead.email} is not a valid email`);
      return;
    }

    if (seenEmails.has(normalizedEmail)) {
      errors.push(`Line ${line}: duplicate email ${lead.email}`);
      return;
    }

    seenEmails.add(normalizedEmail);
    parsedRows.push({ ...lead, email: normalizedEmail });
  });

  return { rows: parsedRows, errors };
}

export function downloadCsvTemplate(filename: string, rows: string[][]) {
  const csv = rows.map(row => row.map(escapeCsvCell).join(',')).join('\r\n');
  const element = document.createElement('a');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  element.href = url;
  element.download = filename;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(url);
}

function splitCsv(data: string[][]): { headers: string[]; rows: string[][] } {
  const cleanRows = data.map(row => row.map(cell => String(cell ?? '').trim()));
  const firstRow = cleanRows[0] ?? [];
  const hasHeader = firstRow.some(cell => [
    'agency',
    'e-mail address',
    'email',
    'email address',
    'first name',
    'full name',
    'rank',
    'rank / title',
    'section',
  ].includes(normalizeHeader(cell)));
  return {
    headers: hasHeader ? firstRow : [],
    rows: hasHeader ? cleanRows.slice(1) : cleanRows,
  };
}

function buildHeaderMap(headers: string[]): Record<string, number> {
  return headers.reduce<Record<string, number>>((acc, header, index) => {
    acc[normalizeHeader(header)] = index;
    return acc;
  }, {});
}

function readCell(row: string[], columns: Record<string, number>, aliases: string[]): string {
  for (const alias of aliases) {
    const index = columns[normalizeHeader(alias)];
    if (index !== undefined)
      return row[index]?.trim() ?? '';
  }
  return '';
}

function normalizeHeader(header: string): string {
  return header.trim().toLowerCase().replace(/\s+/g, ' ');
}

function splitFullName(fullName: string): [string, string] {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0)
    return ['', ''];
  if (parts.length === 1)
    return [parts[0], ''];
  return [parts[0], parts.slice(1).join(' ')];
}

function normalizeRosterSection(section: string): AgencyRosterCsvRow['section'] {
  const normalized = section.trim().toLowerCase();
  return normalized.startsWith('max') || normalized.includes('extension') ?
    'maxOfficers' :
    'minOfficers';
}

function getMissingOfficerFields(row: AgencyOfficerFormRow): string[] {
  return Object.entries(row)
    .filter(([_key, value]) => !value.trim())
    .map(([key]) => key);
}

function isBlankRow(row: string[]): boolean {
  return row.every(cell => !String(cell ?? '').trim());
}

function escapeCsvCell(cell: string): string {
  return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
}
